import { Button, Modal, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useRef, useState } from "react";
import { PageHeader } from "../../../components/PageHeader/index.js";
import {
  wpSyncerService,
  type WpSyncerJobStatus,
  type WpSyncerRunSummary,
} from "../../../api/wpSyncer.service.js";
import { TriggerSyncModal } from "./TriggerSyncModal.js";
import styles from "./WpSyncer.module.css";

const ACTIVE_RUN_KEY = "wp-syncer:active-run-id";
const STATUS_POLL_INTERVAL_MS = 5000;
const RUNS_LIST_POLL_INTERVAL_MS = 10000;
const RUNS_LIST_LIMIT = 25;

function formatTimestamp(epochMs: number): string {
  return new Date(epochMs).toLocaleString();
}

function formatDuration(ms: number): string {
  if (ms <= 0) return "—";
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

function StatusBadge({ status }: { status: WpSyncerJobStatus | null }) {
  if (!status) return <span className={`${styles.statusBadge} ${styles.queued}`}>—</span>;
  const tone =
    status.state === "running"
      ? styles.running
      : status.state === "succeeded"
        ? styles.succeeded
        : status.state === "failed"
          ? styles.failed
          : styles.queued;
  return <span className={`${styles.statusBadge} ${tone}`}>{status.state.toUpperCase()}</span>;
}

function RawStatusBadge({ status }: { status: WpSyncerRunSummary["status"] }) {
  const tone =
    status === "running"
      ? styles.running
      : status === "succeeded"
        ? styles.succeeded
        : status === "failed" || status === "cancelled"
          ? styles.failed
          : styles.queued;
  return <span className={`${styles.statusBadge} ${tone}`}>{status.toUpperCase()}</span>;
}

function SourceBadge({ source }: { source: WpSyncerRunSummary["source"] }) {
  if (source !== "azure") return null;
  return <span className={styles.externalTag}>external</span>;
}

export default function WpSyncerPage() {
  const [runId, setRunId] = useState<string | null>(() =>
    typeof window !== "undefined" ? window.localStorage.getItem(ACTIVE_RUN_KEY) : null,
  );
  const [status, setStatus] = useState<WpSyncerJobStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [logLines, setLogLines] = useState<string[]>([]);
  const [logEndState, setLogEndState] = useState<string | null>(null);
  const [logError, setLogError] = useState<string | null>(null);
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancellingRuns, setCancellingRuns] = useState<Record<string, boolean>>({});
  const [recentRuns, setRecentRuns] = useState<WpSyncerRunSummary[]>([]);
  const [recentRunsError, setRecentRunsError] = useState<string | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const logPreRef = useRef<HTMLPreElement>(null);

  const pollRecentRuns = useCallback(async () => {
    try {
      const response = await wpSyncerService.listRuns(RUNS_LIST_LIMIT);
      setRecentRuns(response.runs);
      setRecentRunsError(null);
    } catch (err) {
      setRecentRunsError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    pollRecentRuns();
    const interval = setInterval(pollRecentRuns, RUNS_LIST_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [pollRecentRuns]);

  const pollStatus = useCallback(async (id: string) => {
    try {
      const response = await wpSyncerService.getStatus(id);
      setStatus(response.status);
      setStatusError(null);
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    if (!runId) {
      setStatus(null);
      return;
    }
    pollStatus(runId);
    const interval = setInterval(() => pollStatus(runId), STATUS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [runId, pollStatus]);

  useEffect(() => {
    if (!runId) {
      setLogLines([]);
      setLogEndState(null);
      setLogError(null);
      return;
    }

    setLogLines([]);
    setLogEndState(null);
    setLogError(null);

    const subscription = wpSyncerService.subscribeToLog(runId, {
      onLine: line => setLogLines(prev => [...prev, line]),
      onEnd: state => setLogEndState(state),
      onError: err => setLogError(err.message),
    });

    return () => subscription.close();
  }, [runId]);

  useEffect(() => {
    if (logPreRef.current) {
      logPreRef.current.scrollTop = logPreRef.current.scrollHeight;
    }
  }, [logLines]);

  const handleTriggered = (newRunId: string) => {
    setRunId(newRunId);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ACTIVE_RUN_KEY, newRunId);
    }
    setTriggerOpen(false);
    pollRecentRuns();
  };

  const handleTriggerConflict = (existingRunId: string | null) => {
    setTriggerOpen(false);
    if (existingRunId) {
      selectRun(existingRunId);
    }
    pollRecentRuns();
    setToast({
      message: "A sync is already in progress. Showing the active run.",
      type: "info",
    });
  };

  const selectRun = (selectedRunId: string) => {
    setRunId(selectedRunId);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ACTIVE_RUN_KEY, selectedRunId);
    }
  };

  const handleCancel = async () => {
    if (!runId) return;
    setCancelling(true);
    try {
      await wpSyncerService.cancel(runId);
      await pollStatus(runId);
    } finally {
      setCancelling(false);
    }
  };

  const handleRowCancel = useCallback(
    async (targetRunId: string) => {
      setCancellingRuns(prev => ({ ...prev, [targetRunId]: true }));
      try {
        const response = await wpSyncerService.cancel(targetRunId);
        await pollRecentRuns();
        if (targetRunId === runId) {
          await pollStatus(runId);
        }
        if (!response.cancelled) {
          setToast({
            message: response.reason
              ? `Could not cancel run: ${response.reason}`
              : "Could not cancel run.",
            type: "error",
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setToast({ message, type: "error" });
      } finally {
        setCancellingRuns(prev => {
          const next = { ...prev };
          delete next[targetRunId];
          return next;
        });
      }
    },
    [pollRecentRuns, pollStatus, runId],
  );

  const clearActiveRun = () => {
    setRunId(null);
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(ACTIVE_RUN_KEY);
    }
  };

  const performResetState = async () => {
    setResetting(true);
    try {
      const response = await wpSyncerService.resetState();
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(ACTIVE_RUN_KEY);
      }
      setRunId(null);
      setStatus(null);
      setStatusError(null);
      setLogLines([]);
      setLogEndState(null);
      setLogError(null);
      await pollRecentRuns();
      setResetConfirmOpen(false);
      setToast({
        message: `Cleared ${response.clearedRuns} run(s).`,
        type: "success",
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setToast({ message, type: "error" });
    } finally {
      setResetting(false);
    }
  };

  const isRunning = status?.state === "running";

  return (
    <div className={styles.page}>
      <PageHeader
        title="WP Syncer"
        subtitle="Trigger and monitor MySQL → SQLite → Postgres syncs"
      />

      <div className={styles.toolbar}>
        <Button color="primary" onClick={() => setTriggerOpen(true)} disabled={isRunning}>
          Trigger sync
        </Button>
        {runId && !isRunning && (
          <Button color="default" variant="outlined" onClick={clearActiveRun}>
            Clear current run
          </Button>
        )}
        {isRunning && (
          <Button color="default" variant="outlined" onClick={handleCancel} disabled={cancelling}>
            {cancelling ? "Cancelling…" : "Cancel run"}
          </Button>
        )}
        <Button
          color="error"
          variant="contained"
          onClick={() => setResetConfirmOpen(true)}
          disabled={resetting}
        >
          {resetting ? "Resetting…" : "Reset Sync State"}
        </Button>
      </div>

      <div className={styles.recentRunsSection}>
        <h3 className={styles.sectionHeading}>Recent runs</h3>
        {recentRunsError && <div className={styles.errorBanner}>{recentRunsError}</div>}
        {recentRuns.length === 0 ? (
          <div className={styles.idle}>
            No runs yet. Trigger one to start a MySQL → SQLite → Postgres sync.
          </div>
        ) : (
          <table className={styles.runsTable}>
            <thead>
              <tr>
                <th>Status</th>
                <th>Source</th>
                <th>Run ID</th>
                <th>Started</th>
                <th>Duration</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {recentRuns.map(run => (
                <tr key={run.runId} className={run.runId === runId ? styles.activeRow : undefined}>
                  <td>
                    <RawStatusBadge status={run.status} />
                  </td>
                  <td>
                    <SourceBadge source={run.source} />
                  </td>
                  <td className={styles.runIdCell}>
                    <div>{run.runId}</div>
                    {run.executionName && run.executionName !== run.runId && (
                      <div className={styles.executionNameSubtext}>{run.executionName}</div>
                    )}
                  </td>
                  <td>{run.startedAt ? formatTimestamp(run.startedAt) : "—"}</td>
                  <td>{run.durationMs ? formatDuration(run.durationMs) : "—"}</td>
                  <td>
                    <div className={styles.rowActions}>
                      {run.runId === runId ? (
                        <span className={styles.activeRowMarker}>Selected</span>
                      ) : (
                        <Button
                          color="default"
                          variant="outlined"
                          onClick={() => selectRun(run.runId)}
                        >
                          View
                        </Button>
                      )}
                      {run.status === "running" && (
                        <Button
                          color="error"
                          variant="outlined"
                          onClick={() => handleRowCancel(run.runId)}
                          disabled={Boolean(cancellingRuns[run.runId])}
                        >
                          {cancellingRuns[run.runId] ? "Cancelling…" : "Cancel"}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!runId && recentRuns.length > 0 && (
        <div className={styles.idle}>
          Select a run above to view its log, or trigger a new sync.
        </div>
      )}

      {runId && (
        <>
          <div className={styles.statusCard}>
            <div>
              <strong>Run</strong> — <StatusBadge status={status} />
            </div>
            <div className={styles.statusGrid}>
              <div className={styles.statusLabel}>Run ID</div>
              <div className={styles.statusValue}>{runId}</div>

              {status?.state === "running" && (
                <>
                  <div className={styles.statusLabel}>Started</div>
                  <div className={styles.statusValue}>{formatTimestamp(status.startedAt)}</div>
                </>
              )}

              {status?.state === "succeeded" && (
                <>
                  <div className={styles.statusLabel}>Duration</div>
                  <div className={styles.statusValue}>{formatDuration(status.durationMs)}</div>
                </>
              )}

              {status?.state === "failed" && (
                <>
                  <div className={styles.statusLabel}>Error</div>
                  <div className={styles.statusValue}>{status.error}</div>
                </>
              )}

              {statusError && (
                <>
                  <div className={styles.statusLabel}>Status fetch error</div>
                  <div className={styles.statusValue}>{statusError}</div>
                </>
              )}
            </div>
          </div>

          <div className={styles.logSection}>
            <div className={styles.logHeading}>
              Live log {logEndState ? `(${logEndState})` : isRunning ? "(streaming…)" : ""}
            </div>
            {logError && <div className={styles.errorBanner}>{logError}</div>}
            <pre ref={logPreRef} className={styles.logPre}>
              {logLines.length === 0 ? "Waiting for log output…" : logLines.join("\n")}
            </pre>
          </div>
        </>
      )}

      <TriggerSyncModal
        isOpen={triggerOpen}
        onClose={() => setTriggerOpen(false)}
        onTriggered={handleTriggered}
        onConflict={handleTriggerConflict}
      />

      <Modal
        isOpen={resetConfirmOpen}
        onClose={() => (resetting ? undefined : setResetConfirmOpen(false))}
        title="Reset wp-syncer state?"
        size="small"
        blurBackground
      >
        <div className={styles.modalBody}>
          <p>
            This clears all run status keys in the platform-api Redis. Active runs will become
            unobservable from this page. Use only when the status display is stuck.
          </p>
          <div className={styles.modalActions}>
            <Button
              color="default"
              variant="outlined"
              onClick={() => setResetConfirmOpen(false)}
              disabled={resetting}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={performResetState}
              disabled={resetting}
            >
              {resetting ? "Resetting…" : "Reset state"}
            </Button>
          </div>
        </div>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
